import { MappedClass, MappedPropertyArray, MappedPropertyClass } from '@h5s/util-object';
import { CashRegisterInvoiceHolder } from './cash-register-invoice-holder';
import { CashRegisterItem } from './cash-register-item';
import { CashRegisterPayment } from './cash-register-payment';
import {
  Document,
  DocumentBody,
  DocumentFooter,
  DocumentHeader,
  DocumentItem,
  ItemType,
  DocumentPayment,
  PaymentType,
  PrintRow,
} from '@h5s/dtos-api';
import { CashRegisterItemType } from './cash-register-item-type.enum';
import { Big } from 'big.js';
import { CashRegisterPaymentType } from './cash-register-payment-type.enum';
import { splitByLength } from '@h5s/util-string';
import { Department } from '../../settings/entities';
import { resolveDocumentNaturaCode, resolveExpectedVatIndex } from './z-printer-natura-mapping';
import { ZPrinterManufacturer } from './z-printer-paper-manufacturer';

@MappedClass()
export class CashRegisterReceipt {
  @MappedPropertyClass(() => CashRegisterInvoiceHolder)
  invoiceHolder: CashRegisterInvoiceHolder | null;

  @MappedPropertyArray(() => CashRegisterItem)
  items: CashRegisterItem[];

  @MappedPropertyArray(() => CashRegisterPayment)
  payments: CashRegisterPayment[];
}

export function mapCashRegisterReceiptToZPrinterDocument(
  receipt: CashRegisterReceipt,
  manufacturer: ZPrinterManufacturer,
): Document {
  /*
   * Header
   */
  const documentHeader: DocumentHeader = {
    lottery_code: receipt.invoiceHolder?.lotteryCode ?? '',
  };

  const items: DocumentItem[] = [];
  let itemsTotal = new Big(0);

  /*
   * Body
   */

  // Elementi del conto
  for (const item of receipt.items) {
    const result = prepareItemDescriptionAndNotes(item.description);

    // In questo momento ignoriamo i comment perché non sappiamo come aggiungerli
    if (item.type !== CashRegisterItemType.Comment) {
      const departmentIndex = item.department?.cashRegisterCode
        ? parseInt(item.department?.cashRegisterCode)
        : undefined;
      // Stessa guardia di ZPrinterFiscalConfigDepartmentIn.fromDepartment: mai un department_index NaN verso la stampante
      if (Number.isNaN(departmentIndex)) {
        throw new Error(
          `[mapCashRegisterReceiptToZPrinterDocument] Codice registratore di cassa non numerico per il reparto '${item.department?.description}': '${item.department?.cashRegisterCode}'`,
        );
      }

      items.push({
        // Prezzo totale
        price: item.amount?.times(100).toNumber(),
        // Prezzo del singolo
        unit_price: item.amount?.times(100).toNumber(),
        // A quanto corrisponde un singolo prodotto
        units: 1,
        department_index: departmentIndex,
        description: result?.itemDescription,
        note: result?.notes,
        item_type: cashRegisterItemTypeToItemType[item.type],
        // Numero di prodotti
        quantity: 1,
        ...buildItemVatFields(item.department, manufacturer),
      });

      // Aggiorno il totale degli elementi del conto
      if (item.amount) {
        itemsTotal = itemsTotal.plus(item.amount);
      }
    }
  }

  const invoiceHolderLines = prepareInvoiceHolderRows(receipt.invoiceHolder);

  const documentBody: DocumentBody = {
    items: items,
    extra: invoiceHolderLines,
    total: itemsTotal.times(100).toNumber(),
  };

  /*
   * Footer
   */
  const payments: DocumentPayment[] = [];
  let paymentsTotal = new Big(0);
  for (const payment of receipt.payments) {
    payments.push({
      amount: payment.amount?.times(100).toNumber(),
      description: payment.description ?? undefined,
      type: cashRegisterPaymentTypeToPaymentType[payment.type],
    });

    // Aggiorno il totale dei pagamenti
    if (payment.amount) {
      paymentsTotal = paymentsTotal.plus(payment.amount);
    }
  }

  const documentFooter: DocumentFooter = {
    payments: payments,
    total: itemsTotal.times(100).toNumber(),
  };

  return {
    organization_id: receipt.invoiceHolder?.fiscalCode ?? '',
    document_type: 'receipt',
    header: documentHeader,
    body: documentBody,
    footer: documentFooter,
  };
}

// Nessuno dei tre campi incide sulla stampa: l'IVA reale è quella del department_index.
// Servono solo al ZBridge per il controllo incrociato (warning Printer.Vat.* se non coincidono con la programmazione nota).
// vat_index si applica solo su Custom/ItalRetail (fonte: doc ZBridge "Aliquote e nature IVA").
function buildItemVatFields(
  department: Department | null,
  manufacturer: ZPrinterManufacturer,
): Pick<DocumentItem, 'vat_rate' | 'vat_nature' | 'vat_index'> {
  const vatRate = department?.vatRate;
  if (!vatRate) return {};

  const usesVatIndex = manufacturer === ZPrinterManufacturer.Custom || manufacturer === ZPrinterManufacturer.ItalRetail;

  return {
    vat_rate: vatRate.naturaTypeOnZeroVat ? undefined : vatRate.rate.times(100).toNumber(),
    vat_nature: vatRate.naturaTypeOnZeroVat ? resolveDocumentNaturaCode(vatRate.naturaTypeOnZeroVat) : undefined,
    // Stesso indice con cui viene costruita la fiscal-config, non cashRegisterCode grezzo:
    // sulle righe a natura quello configurato dall'utente non è l'indice che la cassa usa davvero
    vat_index: usesVatIndex ? resolveExpectedVatIndex(vatRate, manufacturer) : undefined,
  };
}

const MAX_ROW_LENGHT = 25;

interface ItemDescriptions {
  itemDescription?: string;
  notes?: string;
}

/**
 * Prepara le righe di stampa per la stampa su registratore
 *
 * @param description Riga da formattare
 * @returns Oggetto contenente le descrizioni formattate
 */
function prepareItemDescriptionAndNotes(description: string | null): ItemDescriptions | null {
  if (!description) {
    return null;
  }

  if (description.length <= MAX_ROW_LENGHT) {
    return {
      itemDescription: description,
    };
  }

  const result: ItemDescriptions = {};
  const test = splitByLength(description, MAX_ROW_LENGHT);

  // Come descrizione della riga di vendita mettiamo la 1° riga generata
  result.itemDescription = test[0];

  /**
   * Poi la rimuoviamo e come note mettiamo le restanti, in modo che il risultato sia qualcosa del tipo:
   * Matrimoniale Standard: 22%        1350, 00     [1]
   *    Bed and Breakfast                           [2]
   * 18-08 30-05                                    [3]
   *
   * Dove [1] è la riga di vendita, mentre [2] e [3] sono le righe descrittive
   */
  test.splice(0, 1);

  /**
   * Per poter stampare più righe descrittive sulla stessa riga di vendita,
   * occorre formattare la stringa, dividendo ogni riga che si vuole stampare con "|".
   * Tipo, valorizzando il campo note con:"Nota 1|Nota 2|Nota 3", stamperà:
   * Nota 1
   * Nota 2
   * Nota 3
   */
  result.notes = test.join('|');

  return result;
}

/**
 * Prepara le righe non fiscali per la stampa dell'intestatario
 *
 * @param invoiceHolder Intestatario
 */
function prepareInvoiceHolderRows(invoiceHolder: CashRegisterInvoiceHolder | null): PrintRow[] {
  if (!invoiceHolder) return [];

  const INVOICE_HOLDER_SEPARATOR = '------------------';

  /* Rappresenta una riga vuota
     Attenzione: non usare stringa vuota perché causa un errore in ItalRetail Pr4, rallentando tantissimo la stampa
   */
  const EMPTY_ROW = ' ';

  const result: PrintRow[] = [];

  /**
   * Il backend ritorna sempre 5 righe di descrizione dell'intestatario.
   * Nel caso ci siano tutti i campi, ritorna:
   *
   * Mario Rossi    [Nominativo]
   * Via Verdi, 2   [Indirizzo]
   * 47924 Rimini   [CAP e città]
   * 012345678      [P. IVA]
   * ''             [Non usato]
   *
   * Ma può anche ritornare:
   *
   * Mario Rossi    [Nominativo]
   * ''             [Indirizzo]
   * ''             [CAP e città]
   * ''             [P. IVA]
   * ''             [Non usato]
   *
   * O anche:
   *
   * ''             [Nominativo]
   * ''             [Indirizzo]
   * ''             [CAP e città]
   * ''             [P. IVA]
   * ''             [Non usato]
   *
   * Quindi filtriamo in modo da escludere tutte le righe non usate, così da non avere degli spazi vuoti in stampa
   */
  const filteredInvoiceHolderRows = invoiceHolder.lines.filter((l) => l);

  // Se dopo aver filtrato le righe vuote, non ne abbiamo altre, usciamo
  if (!filteredInvoiceHolderRows || filteredInvoiceHolderRows.length <= 0) return [];

  // Aggiungiamo una riga, per separare le righe dell'intestatario dai prodotti
  result.push({
    text: EMPTY_ROW,
  });
  result.push({
    text: INVOICE_HOLDER_SEPARATOR,
  });

  // Aggiungiamo le righe filtrate alle righe da inviare alla stampante
  result.push(
    ...filteredInvoiceHolderRows.map((r) => {
      return { text: r } as PrintRow;
    }),
  );
  // Aggiungiamo una riga, per separare le righe dell'intestatario dal footer
  result.push({
    text: INVOICE_HOLDER_SEPARATOR,
  });
  result.push({
    text: EMPTY_ROW,
  });
  return result;
}

const cashRegisterItemTypeToItemType: Record<CashRegisterItemType, ItemType> = {
  [CashRegisterItemType.Comment]: 'standard',
  [CashRegisterItemType.Discount]: 'discount',
  [CashRegisterItemType.Standard]: 'standard',
  [CashRegisterItemType.Surcharge]: 'surcharge',
};

const cashRegisterPaymentTypeToPaymentType: Record<CashRegisterPaymentType, PaymentType> = {
  [CashRegisterPaymentType.Cash]: 'cash',
  [CashRegisterPaymentType.Card]: 'card',
  [CashRegisterPaymentType.Cheque]: 'cheque',
  [CashRegisterPaymentType.NotCollected]: 'not_collected',
  [CashRegisterPaymentType.NotCollectedInvoice]: 'not_collected_invoice',
  [CashRegisterPaymentType.PayDiscount]: 'pay_discount',
  [CashRegisterPaymentType.WireTransfer]: 'wire_transfer',
};
